import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFlag, FaTimes, FaPaperPlane } from 'react-icons/fa';
import toast from 'react-hot-toast';
import AnswerOption from '../UI/AnswerOption';
import api from '../../api/axios';

function ReportModal({ question, onClose }) {
  const [raison, setRaison]     = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async () => {
    if (raison.trim().length < 5) {
      toast.error('Décris le problème en au moins 5 caractères.');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/questions/${question._id || question.id}/report`, {
        raison: raison.trim(),
        reponse_suggeree: suggestion.trim() || undefined,
      });
      toast.success('Signalement envoyé. Merci !');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors du signalement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="w-full max-w-sm rounded-2xl p-5 space-y-4"
        style={{
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)',
          border: '1.5px solid rgba(239,68,68,0.4)',
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaFlag size={14} className="text-red-400" />
            <p className="font-black text-white" style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem' }}>
              SIGNALER UNE ERREUR
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <FaTimes size={15} />
          </button>
        </div>

        {/* Question rappel */}
        <p className="text-white/60 text-xs leading-relaxed border-l-2 border-red-400/40 pl-3">
          {question.question || question.intitule}
        </p>

        {/* Raison */}
        <div>
          <label className="text-white/50 text-xs uppercase tracking-widest mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Problème constaté *
          </label>
          <textarea
            rows={3}
            value={raison}
            onChange={(e) => setRaison(e.target.value)}
            placeholder="Ex : La bonne réponse est le PSG, pas Saint-Étienne..."
            className="w-full rounded-xl p-3 text-sm text-white resize-none outline-none"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
          />
        </div>

        {/* Suggestion réponse */}
        <div>
          <label className="text-white/50 text-xs uppercase tracking-widest mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Réponse correcte (optionnel)
          </label>
          <input
            type="text"
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="Ex : Paris Saint-Germain"
            className="w-full rounded-xl p-3 text-sm text-white outline-none"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
          />
        </div>

        {/* Bouton */}
        <button
          onClick={submit}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all"
          style={{
            background: loading ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.85)',
            color: '#fff',
            fontFamily: "'Rajdhani', sans-serif",
            letterSpacing: '0.05em',
          }}
        >
          <FaPaperPlane size={12} />
          {loading ? 'Envoi...' : 'ENVOYER LE SIGNALEMENT'}
        </button>
      </motion.div>
    </motion.div>
  );
}

function QuestionCard({
  question,
  questionIndex,
  totalQuestions,
  selectedAnswer,
  correctAnswer,
  revealed,
  onAnswer,
  disabled,
  eliminatedOptions = [],
}) {
  const [reportOpen, setReportOpen] = useState(false);

  if (!question) return null;

  const questionText = question.question || question.texte || question.intitule || '';
  const options = question.options || question.reponses || [];

  return (
    <>
      <motion.div
        key={question._id || questionIndex}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.28 }}
        className="flex flex-col gap-3"
      >
        {/* Barre de progression + meta */}
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-white/50" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Question <span className="text-white font-bold">{questionIndex + 1}</span>/{totalQuestions}
          </span>
          {question.categorie && (
            <span
              className="text-xs uppercase tracking-wider px-3 py-1 rounded-full font-bold"
              style={{
                background: 'rgba(240,180,41,0.12)',
                border: '1px solid rgba(240,180,41,0.25)',
                color: '#f0b429',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              {question.categorie}
            </span>
          )}
        </div>

        {/* Barre de progression */}
        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <motion.div
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #f0b429, #fbbf24)' }}
            initial={{ width: `${(questionIndex / totalQuestions) * 100}%` }}
            animate={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Carte question */}
        <motion.div
          className="rounded-2xl p-5 space-y-3"
          style={{
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.09)',
            backdropFilter: 'blur(8px)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.08 }}
        >
          {/* Image si présente */}
          {question.image_url && (
            <motion.img
              src={question.image_url}
              alt="Question image"
              className="w-full rounded-lg max-h-64 object-cover"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            />
          )}

          <p
            className="text-white leading-relaxed font-semibold"
            style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.05rem' }}
          >
            {questionText}
          </p>
        </motion.div>

        {/* Difficulté */}
        {question.difficulte && (
          <div className="flex items-center gap-2">
            <span className="text-white/35 text-xs">Difficulté :</span>
            <div className="flex gap-1">
              {[1, 2, 3].map((d) => {
                const filled = d <= (question.difficulte === 'facile' ? 1 : question.difficulte === 'moyen' ? 2 : 3);
                return (
                  <div
                    key={d}
                    className="w-2.5 h-2.5 rounded-full"
                    style={{
                      background: filled ? '#f0b429' : 'rgba(255,255,255,0.15)',
                      boxShadow: filled ? '0 0 6px rgba(240,180,41,0.5)' : 'none',
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Options de réponse */}
        <div className="flex flex-col gap-2.5 mt-1">
          {options.map((option, idx) => {
            const optionText = typeof option === 'string' ? option : option.texte || String(option);
            const isCorrect  = correctAnswer === idx;
            const eliminated = eliminatedOptions.includes(idx);

            return (
              <div key={idx} style={{ opacity: eliminated ? 0.18 : 1, transition: 'opacity 0.4s', pointerEvents: eliminated ? 'none' : 'auto' }}>
                <AnswerOption
                  index={idx}
                  text={optionText}
                  selected={selectedAnswer === idx}
                  correct={isCorrect}
                  incorrect={revealed && selectedAnswer === idx && !isCorrect}
                  revealed={revealed}
                  disabled={disabled || revealed || eliminated}
                  onClick={() => !disabled && !revealed && !eliminated && onAnswer(idx)}
                />
              </div>
            );
          })}
        </div>

        {/* Bouton signaler — visible après révélation */}
        <AnimatePresence>
          {revealed && (
            <motion.button
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.3 }}
              onClick={() => setReportOpen(true)}
              className="flex items-center gap-1.5 self-end text-xs py-1.5 px-3 rounded-lg transition-all"
              style={{
                color: 'rgba(239,68,68,0.6)',
                border: '1px solid rgba(239,68,68,0.2)',
                background: 'rgba(239,68,68,0.07)',
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              <FaFlag size={10} />
              Signaler une erreur
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {reportOpen && (
          <ReportModal question={question} onClose={() => setReportOpen(false)} />
        )}
      </AnimatePresence>
    </>
  );
}

export default QuestionCard;
